import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { floatingOverlayPortalSystem } from './FloatingOverlayPortalContext';

interface FloatingOverlayHostProps {
  /** Content to render inside the host (rare; most overlays should use FloatingOverlayPortal). */
  children?: ReactNode;
  /** Additional CSS classes for the host container. */
  className?: string;
}

/**
 * FloatingOverlayHost — provides a portal target for floating overlays.
 *
 * - Fixed positioned to cover the viewport.
 * - Pointer events are disabled on the host layer; overlay content must opt-in
 *   with `pointer-events-auto` on its own elements.
 */
export function FloatingOverlayHost({ children, className }: FloatingOverlayHostProps) {
  const PortalHost = floatingOverlayPortalSystem.PortalHost;

  return (
    <PortalHost
      className={className}
      render={({ containerRef, children: hostChildren, className: hostClassName }) => (
        <>
          {hostChildren}
          <div
            className={cn('fixed inset-0 z-50 pointer-events-none', hostClassName)}
            role="presentation"
          >
            <div ref={containerRef} className="h-full w-full pointer-events-none" />
          </div>
        </>
      )}
    >
      {children}
    </PortalHost>
  );
}
